import React from 'react';
import { motion } from 'framer-motion';
import {
  FaBullseye,
  FaFlask,
  FaUserGraduate,
  FaSlidersH,
  FaCheckCircle,
  FaChartLine,
  FaDumbbell,
  FaUsers,
  FaHeartbeat,
  FaAppleAlt,
  FaUserCheck,
} from 'react-icons/fa';
import image1 from '../../assets/image1.png';
import image3 from '../../assets/image3.png';

const whyChoosePoints = [
  {
    title: 'Goal-focused training',
    icon: <FaBullseye className="text-red-500 text-xl flex-shrink-0" />,
  },
  {
    title: 'Science-backed approach',
    icon: <FaFlask className="text-red-500 text-xl flex-shrink-0" />,
  },
  {
    title: 'Professional coaching',
    icon: <FaUserGraduate className="text-red-500 text-xl flex-shrink-0" />,
  },
  {
    title: 'Individualized programming',
    icon: <FaSlidersH className="text-red-500 text-xl flex-shrink-0" />,
  },
  {
    title: 'Technique-focused training',
    icon: <FaCheckCircle className="text-red-500 text-xl flex-shrink-0" />,
  },
  {
    title: 'Progress tracking',
    icon: <FaChartLine className="text-red-500 text-xl flex-shrink-0" />,
  },
  {
    title: 'Strength, muscle, fat loss, conditioning and general fitness options',
    icon: <FaDumbbell className="text-red-500 text-xl flex-shrink-0" />,
  },
  {
    title: 'Supportive training environment',
    icon: <FaUsers className="text-red-500 text-xl flex-shrink-0" />,
  },
];

const gymHighlights = [
  { icon: <FaDumbbell className="text-red-500 text-xl" />, label: 'Strength Zone' },
  { icon: <FaHeartbeat className="text-rose-500 text-xl" />, label: 'Cardio Suite' },
  { icon: <FaAppleAlt className="text-green-500 text-xl" />, label: 'Custom Diet' },
  { icon: <FaUserCheck className="text-blue-400 text-xl" />, label: '1-on-1 Coaching' },
];

const WhyChooseUs = () => {
  const transition = { type: 'spring', duration: 2.5 };

  const fadeLeft = {
    hidden: { opacity: 0, x: -60 },
    visible: { opacity: 1, x: 0, transition },
  };
  const fadeRight = {
    hidden: { opacity: 0, x: 60 },
    visible: { opacity: 1, x: 0, transition },
  };

  return (
    <section className="relative px-4 md:px-12 py-16 text-white overflow-hidden max-w-7xl mx-auto" id="why-choose-us">
      <div className="absolute w-80 h-80 bg-red-600/15 blur-[160px] rounded-full top-1/2 left-10 -z-10" />

      {/* Section Header */}
      <motion.div
        className="text-center max-w-3xl mx-auto mb-16"
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={transition}
        viewport={{ once: true }}
      >
        <span className="inline-block bg-red-600/15 border border-red-500/30 text-red-500 text-xs font-bold uppercase tracking-widest px-4 py-1.5 rounded-full mb-3 shadow-lg">
          3 Units Across Coimbatore
        </span>
        <div className="text-3xl sm:text-5xl font-black uppercase tracking-wide mb-3">
          <span className="text-transparent [-webkit-text-stroke:_1.5px_white]">WHY </span>
          <span className="text-red-500">IDOL FITNESS ZONE</span>
        </div>
        <p className="text-gray-300 text-sm sm:text-base leading-relaxed max-w-2xl mx-auto">
          We combine science-backed methodology, expert coaching, and tailored programs to ensure you reach your maximum potential safely and consistently.
        </p>
      </motion.div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
        {/* Left Column: Images + Badge */}
        <motion.div
          className="lg:col-span-5 relative flex flex-col gap-4 pb-8 sm:pb-10"
          initial="hidden"
          whileInView="visible"
          variants={fadeLeft}
          viewport={{ once: true }}
        >
          <div className="grid grid-cols-2 gap-4">
            <img src={image1} alt="Gym strength area" className="w-full h-48 sm:h-64 md:h-72 object-cover rounded-3xl shadow-2xl border border-white/10" />
            <img src={image3} alt="Gym training session" className="w-full h-48 sm:h-64 md:h-72 object-cover rounded-3xl shadow-2xl sm:mt-8 border border-white/10" />
          </div>
          
          <div className="mt-4 mx-auto bg-gradient-to-r from-red-600 to-red-800 text-white px-5 py-3.5 rounded-2xl shadow-2xl flex items-center gap-4 text-center border border-white/20 w-fit">
            <span className="text-3xl font-extrabold">100%</span>
            <div className="flex flex-col text-left leading-tight">
              <span className="text-[10px] uppercase font-medium text-white/80">Result Driven</span>
              <span className="text-xs font-black uppercase tracking-wide">3 Gym Units</span>
            </div>
          </div>
        </motion.div>

        {/* Right Column: 8 Key Features Grid */}
        <motion.div
          className="lg:col-span-7 flex flex-col gap-6"
          initial="hidden"
          whileInView="visible"
          variants={fadeRight}
          viewport={{ once: true }}
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {whyChoosePoints.map((point, i) => (
              <motion.div
                key={i}
                className="bg-white/5 p-4 rounded-2xl border border-white/10 hover:border-red-500/40 transition-all duration-300 hover:bg-white/10 flex items-start gap-3 group"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.05, ...transition }}
                viewport={{ once: true }}
              >
                <div className="p-2.5 bg-red-600/10 border border-red-500/20 rounded-xl group-hover:bg-red-600/20 transition-colors">
                  {point.icon}
                </div>
                <div>
                  <h4 className="text-sm font-bold text-white group-hover:text-red-400 transition-colors leading-snug flex items-center gap-2">
                    <span className="capitalize">{point.title}</span>
                  </h4>
                  <p className="text-xs text-gray-400 mt-1 leading-relaxed">
                    {point.desc}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Facility Highlights */}
          <div className="pt-4 border-t border-white/10 flex flex-col sm:flex-row items-center gap-4">
            <span className="text-xs font-bold uppercase tracking-widest text-gray-400">Gym Features:</span>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 w-full sm:w-auto">
              {gymHighlights.map((hl, i) => (
                <div key={i} className="flex items-center gap-2 bg-white/5 px-3 py-2 rounded-xl border border-white/10">
                  {hl.icon}
                  <span className="text-xs font-semibold text-gray-200">{hl.label}</span>
                </div>
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default WhyChooseUs;

